import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Chip, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, PhoneIphoneOutlined, CheckCircleOutline,
    LockOutlined, NorthEastOutlined, SouthWestOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    orange:  '#FF9500',
};

const NETWORKS = [
    { value: 'mtn',     label: 'MTN',     color: '#FFCC00', bg: '#FFCC0018', icon: '/images/mtn-new-logo.svg'  },
    { value: 'glo',     label: 'Glo',     color: '#00A650', bg: '#00A65018', icon: '/images/glo.jpg' },
    { value: 'airtel',  label: 'Airtel',  color: '#FF0000', bg: '#FF000018', icon: '/images/airtel-logo.png' },
    { value: '9mobile', label: '9mobile', color: '#00A859', bg: '#00A85918', icon: '/images/9mobile.jpg' },
];

const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

const RECENT_TXS = [
    { id: 1, type: 'debit',  title: 'MTN Airtime',    sub: '080 *** 4521', amount: 1000,  date: 'Today · 8:02 AM',      status: 'success' },
    { id: 2, type: 'debit',  title: 'Airtel Airtime',  sub: '081 *** 3310', amount: 500,   date: 'Yesterday · 3:15 PM',  status: 'success' },
    { id: 3, type: 'debit',  title: 'Glo Airtime',     sub: '081 *** 7722', amount: 200,   date: 'Mon · 10:45 AM',       status: 'success' },
    { id: 4, type: 'debit',  title: 'MTN Airtime',     sub: '080 *** 4521', amount: 2000,  date: 'Sun · 6:30 PM',        status: 'success' },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const AirtimePage: React.FC = () => {
    const navigate  = useNavigate();
    const theme     = useTheme();
    const isDark    = theme.palette.mode === 'dark';

    const [step,       setStep]       = useState<1 | 2>(1);
    const [network,    setNetwork]    = useState('');
    const [phone,      setPhone]      = useState('');
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [amount,     setAmount]     = useState('');
    const [loading,    setLoading]    = useState(false);
    const [done,       setDone]       = useState(false);

    const numAmount = parseFloat(amount) || 0;
    const balance   = 125000;
    const insufficient = numAmount > balance;
    const selectedNet  = NETWORKS.find(n => n.value === network);

    // Nigerian phone validation: 11 digits starting with 0, or 10 digits (without leading 0)
    // Valid prefixes: 070, 080, 081, 090, 091 (MTN/Glo/Airtel/9mobile/Ntel)
    const NG_PHONE_RE = /^(?:0[7-9][0-1]\d{8}|[7-9][0-1]\d{8})$/;
    const phoneClean  = phone.replace(/\s+/g, '');
    const phoneValid  = NG_PHONE_RE.test(phoneClean);
    const phoneError  = phoneTouched && phone.length > 0 && !phoneValid
        ? phoneClean.length < 10
            ? 'Number too short — must be 11 digits'
            : /[^0-9]/.test(phoneClean)
                ? 'Numbers only, no letters or symbols'
                : 'Enter a valid Nigerian number (e.g. 080 1234 5678)'
        : '';

    // ── Theme tokens ──
    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleConfirm = () => {
        setLoading(true);
        setTimeout(() => { setLoading(false); setDone(true); }, 1800);
    };

    const goBack = () => {
        if (step === 1) navigate(-1);
        else setStep(1);
    };

    /* ── SUCCESS ── */
    if (done) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Airtime Sent!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{fmt(numAmount)} airtime sent to</Typography>
                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1rem', mb: .25 }}>{phone}</Typography>
                <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 1 }}>via {selectedNet?.label}</Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 20, px: 2, py: .6, mb: 4 }}>
                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>New Balance: {fmt(balance - numAmount)}</Typography>
                </Box>
                <Box display="flex" gap={2} justifyContent="center">
                    <Button onClick={() => { setDone(false); setStep(1); setAmount(''); setPhone(''); setNetwork(''); }}
                            sx={{ border: `1px solid ${alpha(isDark ? '#fff' : B.dark, .12)}`, color: textMid, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 600, fontSize: '.85rem' }}>
                        Send Again
                    </Button>
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 700, fontSize: '.85rem', boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Done
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 },
                px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.orange, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            Step {step} of 2
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Buy Airtime
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>Bal: {fmt(balance)}</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── STEP 1: Network + Phone + Amount ── */}
                {step === 1 && (
                    <>
                        {/* Network selector */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Select Network</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                {NETWORKS.map(n => (
                                    <Box key={n.value} onClick={() => { setNetwork(n.value); }} sx={{
                                        borderRadius: '14px', p: 1.5, cursor: 'pointer', textAlign: 'center',
                                        background: network === n.value ? alpha(n.color, .12) : alpha(isDark ? '#fff' : B.dark, .03),
                                        border: `1.5px solid ${network === n.value ? alpha(n.color, .4) : dividerC}`,
                                        transition: 'all .18s',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .75,
                                    }}>
                                        <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <img src={n.icon} alt={n.label}
                                                 style={{ width: 36, height: 36, objectFit: 'contain' }}
                                                 onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        </Box>
                                        <Typography sx={{ color: network === n.value ? n.color : textMid, fontSize: '.7rem', fontWeight: 700 }}>{n.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Phone number */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2, border: phoneTouched && phoneError ? `1.5px solid ${alpha(B.red, .4)}` : (phoneTouched && phoneValid ? `1.5px solid ${alpha(B.green, .4)}` : surfaceBdr) }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 1.5, fontWeight: 600 }}>Phone Number</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ background: alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${dividerC}`, borderRadius: '12px', px: 1.5, py: 1.25, flexShrink: 0 }}>
                                    <Typography sx={{ color: textMid, fontSize: '.82rem', fontWeight: 600 }}>+234</Typography>
                                </Box>
                                <input
                                    type="tel" placeholder="080 0000 0000"
                                    value={phone}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9\s]/g, '');
                                        setPhone(val);
                                    }}
                                    onBlur={() => setPhoneTouched(true)}
                                    style={{
                                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                        color: textPri, fontSize: '1rem', fontWeight: 600,
                                        fontFamily: "'DM Sans', system-ui, sans-serif",
                                    }}
                                />
                                {phoneTouched && phone.length > 0 && (
                                    <Typography sx={{ fontSize: '1rem', flexShrink: 0 }}>
                                        {phoneValid ? '✅' : '❌'}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mt: 1.5, height: '1px', background: `linear-gradient(90deg, ${phoneTouched && phoneError ? B.red : phoneTouched && phoneValid ? B.green : B.orange}, transparent)` }} />
                            {phoneError && (
                                <Typography sx={{ color: B.red, fontSize: '.7rem', mt: 1, fontWeight: 500 }}>
                                    {phoneError}
                                </Typography>
                            )}
                            {phoneTouched && phoneValid && (
                                <Typography sx={{ color: B.green, fontSize: '.7rem', mt: 1, fontWeight: 500 }}>
                                    Valid Nigerian number ✓
                                </Typography>
                            )}
                        </Box>

                        {/* Amount */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 1.5, fontWeight: 600 }}>Amount</Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography sx={{ color: textMid, fontSize: '1.5rem', fontWeight: 300, fontFamily: "'Instrument Serif', serif" }}>₦</Typography>
                                <input
                                    type="number" placeholder="0"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    style={{
                                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                        color: textPri, fontSize: '2rem', fontWeight: 700,
                                        fontFamily: "'Instrument Serif', serif",
                                    }}
                                />
                            </Box>
                            <Box sx={{ mt: 1, height: '1px', background: `linear-gradient(90deg, ${B.orange}, transparent)` }} />
                            <Typography sx={{ color: textFaint, fontSize: '.7rem', mt: 1 }}>Available: {fmt(balance)}</Typography>

                            {/* Quick amounts */}
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mt: 2.5, mb: 1.5, fontWeight: 600 }}>Quick Select</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {QUICK_AMOUNTS.map(q => (
                                    <Chip key={q} label={`₦${q.toLocaleString()}`} onClick={() => setAmount(String(q))}
                                          sx={{
                                              height: 32, fontSize: '.75rem', fontWeight: 600, borderRadius: '20px', cursor: 'pointer',
                                              background: amount === String(q) ? alpha(B.orange, .15) : alpha(isDark ? '#fff' : B.dark, .05),
                                              color: amount === String(q) ? B.orange : textMid,
                                              border: `1px solid ${amount === String(q) ? alpha(B.orange, .35) : dividerC}`,
                                              '&:hover': { background: alpha(B.orange, .1) },
                                          }} />
                                ))}
                            </Box>
                        </Box>

                        <Button fullWidth disabled={!network || !phoneValid || !numAmount}
                                onClick={() => setStep(2)}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.orange}, #FF6B00)`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.orange, .35)}`,
                                    '&:disabled': { opacity: .35, boxShadow: 'none' },
                                    '&:hover': { boxShadow: `0 16px 40px ${alpha(B.orange, .5)}` },
                                }}>
                            Continue
                        </Button>
                    </>
                )}

                {/* ── STEP 2: Confirm ── */}
                {step === 2 && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2.5, fontWeight: 600 }}>Confirm Purchase</Typography>

                            {/* Summary */}
                            {[
                                { label: 'Network',    value: selectedNet?.label ?? '' },
                                { label: 'Phone',      value: phone },
                                { label: 'Amount',     value: fmt(numAmount) },
                                { label: 'Fee',        value: 'Free' },
                            ].map((row, i, arr) => (
                                <React.Fragment key={row.label}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.25}>
                                        <Typography sx={{ color: textFaint, fontSize: '.8rem' }}>{row.label}</Typography>
                                        <Typography sx={{ color: row.label === 'Amount' ? B.orange : textPri, fontWeight: row.label === 'Amount' ? 700 : 600, fontSize: '.85rem' }}>{row.value}</Typography>
                                    </Box>
                                    {i < arr.length - 1 && <Divider sx={{ borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}

                            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: alpha(B.orange, .08), border: `1px solid ${alpha(B.orange, .2)}` }}>
                                <Typography sx={{ color: B.orange, fontSize: '.75rem', fontWeight: 600, textAlign: 'center' }}>
                                    {fmt(numAmount)} will be deducted from your wallet
                                </Typography>
                            </Box>
                        </Box>

                        <Button fullWidth onClick={handleConfirm} disabled={loading}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.orange}, #FF6B00)`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.orange, .35)}`,
                                    '&:disabled': { opacity: .5, boxShadow: 'none' },
                                }}>
                            {loading ? 'Processing…' : `Buy ₦${numAmount.toLocaleString()} Airtime`}
                        </Button>
                    </>
                )}

                {/* ── Recent Transactions ── */}
                <Box sx={{ ...glassCard, mt: 3, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em', color: textPri }}>Recent Airtime</Typography>
                        <Button size="small" onClick={() => navigate('/history')}
                                sx={{ color: B.purple, fontWeight: 600, fontSize: '.7rem', textTransform: 'none', px: 1, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            See all
                        </Button>
                    </Box>

                    {RECENT_TXS.map((tx, i) => (
                        <React.Fragment key={tx.id}>
                            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, background: B.redDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <NorthEastOutlined sx={{ color: B.red, fontSize: 16 }} />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '.84rem', color: textPri, letterSpacing: '-.015em' }}>{tx.title}</Typography>
                                    <Typography sx={{ fontSize: '.68rem', color: textFaint, mt: .15 }}>{tx.sub} · {tx.date}</Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.86rem', color: textPri, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                    −₦{tx.amount.toLocaleString()}
                                </Typography>
                            </Box>
                            {i < RECENT_TXS.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                        </React.Fragment>
                    ))}

                    <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .055)}` }}>
                        <Button fullWidth onClick={() => navigate('/history')}
                                sx={{ color: textFaint, textTransform: 'none', fontWeight: 600, fontSize: '.76rem', py: .75, borderRadius: 2, '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .035), color: textPri } }}>
                            View full history
                        </Button>
                    </Box>
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default AirtimePage;